import { useCallback, useMemo } from "react"

import type { PortalScope } from "@productlane/lib/src/portal-visibility"
import {
	portalScopeFor,
	sameScope,
	togglePortal,
} from "@productlane/lib/src/portal-visibility"
import { mutators } from "@productlane/zero/mutators"

import { analytics } from "@/lib/analytics"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"

export type VisibilityEntityType = "project" | "issue"

interface UpdateVisibilityArgs {
	id: string
	type: VisibilityEntityType
	value: boolean
}

interface HideAllVisibleActionArgs {
	portalInstanceId?: string | null
}

interface BulkUpdateVisibilityArgs {
	ids: readonly string[]
	type: VisibilityEntityType
	value: boolean
}

interface SetPortalVisibilityArgs {
	id: string
	type: VisibilityEntityType
	/** `MAIN_PORTAL_INSTANCE_ID` for the Main portal, otherwise a named portal id. */
	portalId: string
	value: boolean
	/** Every named portal id in the workspace, in workspace order. */
	namedPortalIds: readonly string[]
	current: PortalScope
}

interface PublishOnArgs {
	id: string
	type: VisibilityEntityType
	portalIds: readonly string[]
	namedPortalIds: readonly string[]
}

interface UseVisibilityActionsResult {
	canEdit: boolean
	updateVisibility: (args: UpdateVisibilityArgs) => Promise<void>
	setPortalVisibility: (args: SetPortalVisibilityArgs) => Promise<void>
	/** Publishes on exactly these portals, for a pending item. */
	publishOn: (args: PublishOnArgs) => Promise<void>
	bulkUpdateVisibility: (args: BulkUpdateVisibilityArgs) => Promise<void>
	/**
	 * Hides every visible request in the workspace, or on one named portal.
	 * Runs by filter on the server, so it covers rows this client never synced.
	 */
	hideAllVisible: (args: HideAllVisibleActionArgs) => Promise<void>
}

export const useVisibilityActions = (): UseVisibilityActionsResult => {
	const zero = useZero()
	const { role } = useRole()

	const canEdit = useMemo(() => role !== "VIEWER", [role])

	const updateVisibility = useCallback(
		async ({ id, type, value }: UpdateVisibilityArgs) => {
			if (!canEdit) {
				return
			}

			if (type === "issue") {
				await mutateOk(
					zero.mutate(
						mutators.issues.update({
							id,
							isVisible: value,
						}),
					),
				)
			} else {
				await mutateOk(
					zero.mutate(
						mutators.projects.update({
							id,
							isVisible: value,
						}),
					),
				)
			}

			analytics.track(`update-${type}-visibility`, {
				id,
				value,
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canEdit, zero.mutate],
	)

	/**
	 * Checks or unchecks one portal. Works from where the item shows today, so a
	 * hidden item never brings back the portals it was on before.
	 */
	const setPortalVisibility = useCallback(
		async ({
			id,
			type,
			portalId,
			value,
			namedPortalIds,
			current,
		}: SetPortalVisibilityArgs) => {
			if (!canEdit) return

			const next = togglePortal(current, portalId, value, namedPortalIds)
			if (sameScope(current, next)) return

			const patch = { id, ...next }
			if (type === "issue") {
				await mutateOk(zero.mutate(mutators.issues.update(patch)))
			} else {
				await mutateOk(zero.mutate(mutators.projects.update(patch)))
			}

			analytics.track(`update-${type}-visibility`, {
				id,
				value,
				portal: portalId,
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canEdit, zero.mutate],
	)

	const publishOn = useCallback(
		async ({ id, type, portalIds, namedPortalIds }: PublishOnArgs) => {
			if (!canEdit) return
			const patch = { id, ...portalScopeFor(portalIds, namedPortalIds) }
			if (type === "issue") {
				await mutateOk(zero.mutate(mutators.issues.update(patch)))
			} else {
				await mutateOk(zero.mutate(mutators.projects.update(patch)))
			}
			analytics.track(`update-${type}-visibility`, {
				id,
				value: true,
				portal: portalIds.join(","),
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canEdit, zero.mutate],
	)

	const bulkUpdateVisibility = useCallback(
		async ({ ids, type, value }: BulkUpdateVisibilityArgs) => {
			if (!canEdit || ids.length === 0) {
				return
			}

			if (type === "issue") {
				await mutateOk(
					zero.mutate(
						mutators.issues.bulkChangeVisibility({
							ids: [...ids],
							isVisible: value,
						}),
					),
				)
				analytics.track("bulk-update-issue-visibility", {
					count: ids.length,
					value,
				})
			} else {
				await mutateOk(
					zero.mutate(
						mutators.projects.bulkChangeVisibility({
							ids: [...ids],
							isVisible: value,
						}),
					),
				)
				analytics.track("bulk-update-project-visibility", {
					count: ids.length,
					value,
				})
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canEdit, zero.mutate],
	)

	const hideAllVisible = useCallback(
		async ({ portalInstanceId }: HideAllVisibleActionArgs) => {
			if (!canEdit) return
			await mutateOk(
				zero.mutate(
					mutators.issues.hideAllVisible({
						portalInstanceId: portalInstanceId ?? null,
					}),
				),
			)
			await mutateOk(
				zero.mutate(
					mutators.projects.hideAllVisible({
						portalInstanceId: portalInstanceId ?? null,
					}),
				),
			)
			analytics.track("hide-all-visible-requests", {
				portal: portalInstanceId ?? "root",
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canEdit, zero.mutate],
	)

	return {
		canEdit,
		updateVisibility,
		setPortalVisibility,
		publishOn,
		bulkUpdateVisibility,
		hideAllVisible,
	}
}
